import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LineChart, Line, CartesianGrid, ComposedChart } from "recharts";
import { TrendingUp, TrendingDown, Users, MapPin, IndianRupee, Activity, AlertCircle, CheckCircle, Clock, Camera, ArrowRightLeft, Minus, Download } from "lucide-react";
import StatCard from "../components/StatCard";
import RAGBadge from "../components/RAGBadge";
import { SectionHeader, PageHeader, SnowflakeRequired } from "../components/SectionHeader";
import { useFilters } from "../context/FilterContext";
import ThemeChip from "../components/ThemeChip";
import { appendFyToSearchParams, buildDashboardParams } from "../utils/dashboardFilters";
import { fmtCr, fmtNum, displayTheme } from "../utils/format";

const COLORS = ["#B71C1C", "#16A34A", "#F59E0B", "#64748B", "#8B5CF6", "#0EA5E9", "#D97706", "#059669"];

const fmt = fmtCr;  // alias for backward compat

export default function ExecutiveOverview() {
  const { filters, filterOptions, viewMode, setViewMode, setGlobalLoading, API } = useFilters();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [allocationByTheme, setSanctionedByTheme] = useState([]);
  const [disbTrend, setDisbTrend] = useState([]);
  const [topGrantees, setTopGrantees] = useState([]);
  const [ragSummary, setRagSummary] = useState(null);
  const [newVsOngoing, setNewVsOngoing] = useState(null);
  const [cvaData, setCvaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yoyData, setYoyData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const params = buildDashboardParams(filters, { view_mode: viewMode });

  const fetchAll = async () => {
    setLoading(true);
    setGlobalLoading(true);
    try {
      const [sumRes, themeRes, trendRes, granteeRes, ragRes, noRes, cvaRes, yoyRes] = await Promise.all([
        axios.get(`${API}/executive/summary`, { params }),
        axios.get(`${API}/executive/allocation-by-theme`, { params }),
        axios.get(`${API}/executive/disbursement-trend`, { params }),
        axios.get(`${API}/executive/top-grantees`, { params: { ...params, limit: 10 } }),
        axios.get(`${API}/executive/rag-summary`, { params }),
        axios.get(`${API}/executive/new-vs-ongoing`, { params: buildDashboardParams(filters, { trust: filters.trust, status: filters.status }) }),
        axios.get(`${API}/executive/commitment-vs-achievement`, { params }),
        axios.get(`${API}/executive/yoy-comparison`, { params }).catch(() => ({ data: null })),
      ]);
      setSummary(sumRes.data);
      setSanctionedByTheme(themeRes.data);
      setDisbTrend(trendRes.data);
      setTopGrantees(granteeRes.data);
      setRagSummary(ragRes.data);
      setNewVsOngoing(noRes.data);
      setCvaData(cvaRes.data);
      setYoyData(yoyRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setGlobalLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [filters.fy, filters.theme, filters.trust, filters.status, filters.grantee, viewMode]);

  const sf = filterOptions.data_sources?.fluxx || filterOptions.snowflake_connected;
  const isNewMode = viewMode === "snapshot";
  const isAllGrantsMode = viewMode === "active";
  const isSnapshot = isNewMode;

  // Export handler
  const handleExport = async () => {
    setExporting(true);
    try {
      const exportParams = new URLSearchParams();
      appendFyToSearchParams(exportParams, filters.fy);
      if (filters.theme) exportParams.append("theme", filters.theme);
      if (filters.trust) exportParams.append("trust", filters.trust);
      if (filters.status) exportParams.append("status", filters.status);
      exportParams.append("view_mode", viewMode);
      const response = await axios.get(`${API}/executive/export?${exportParams.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `executive_summary_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  // Delta badge helper
  const DeltaBadge = ({ delta, higherIsBetter = true }) => {
    if (!delta || delta.pct === null) return <span className="text-xs text-slate-500">–</span>;
    const isPositive = delta.pct > 0;
    const isGood = higherIsBetter ? isPositive : !isPositive;
    const Icon = isPositive ? TrendingUp : isPositive === false ? TrendingDown : Minus;
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
        isGood ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"
      }`}>
        <Icon size={10} />
        {isPositive ? "+" : ""}{delta.pct}%
      </span>
    );
  };

  return (
    <div data-testid="executive-overview" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader
          title="Executive Overview"
          subtitle={
            viewMode === "snapshot"
              ? "New grants approved in the selected FY set"
              : viewMode === "active"
                ? "All grants active in the selected FY set"
                : "Ongoing grants active in the selected FY set, excluding newly approved grants"
          }
          badge="01"
        />
        <div className="flex items-center gap-3 flex-wrap">
          {/* Export Button */}
          <button
            onClick={handleExport}
            disabled={exporting || !sf}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            data-testid="export-btn"
          >
            <Download size={14} />
            {exporting ? "Exporting..." : "Export CSV"}
          </button>
          {/* View Mode Toggle */}
          <div data-testid="view-mode-toggle" className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 flex-shrink-0">
            {[
              ["active", "All Grants", ArrowRightLeft],
              ["snapshot", "New", Camera],
              ["ongoing", "Ongoing", Clock],
            ].map(([mode, label, Icon]) => (
              <button
                key={mode}
                data-testid={`toggle-${mode}`}
                onClick={() => setViewMode(mode)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  viewMode === mode
                    ? "bg-white text-[#B71C1C] shadow-sm border border-slate-200"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Icon size={14} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mode Explanation Banner */}
      <div className={`rounded-lg px-4 py-2.5 text-xs flex items-center gap-2 border ${
        viewMode === "snapshot"
          ? "bg-red-50 border-red-100 text-red-700"
          : viewMode === "active"
            ? "bg-blue-50 border-blue-100 text-blue-700"
            : "bg-amber-50 border-amber-100 text-amber-700"
      }`} data-testid="view-mode-banner">
        {viewMode === "snapshot" ? <Camera size={13} className="flex-shrink-0" /> : viewMode === "active" ? <ArrowRightLeft size={13} className="flex-shrink-0" /> : <Clock size={13} className="flex-shrink-0" />}
        {viewMode === "snapshot"
          ? <span><strong>New:</strong> Counts grants approved in any selected FY.</span>
          : viewMode === "active"
            ? <span><strong>All Grants:</strong> Counts grants whose start &amp; end dates overlap any selected FY — includes multi-year grants.</span>
            : <span><strong>Ongoing:</strong> Counts grants active in the selected FY set, excluding grants newly approved in that same selected FY set.</span>
        }
      </div>

      {/* Hero Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="hero-stats">
        <StatCard
          title={isSnapshot ? "Grants Approved" : "Grants Active in FY"}
          value={summary?.total_grants}
          icon={Activity}
          color="red"
          loading={loading}
          onClick={() => navigate("/thematic")}
          tooltip={isSnapshot ? "Grants sanctioned in selected FY (fluxx_grants.fy)" : "Grants whose start/end dates overlap with selected FY"}
        />
        <StatCard
          title={isSnapshot ? "Grantees (Portfolio)" : "Grantees (Active)"}
          value={summary?.active_grantees}
          icon={Users}
          color="blue"
          loading={loading}
          onClick={() => navigate("/grantee")}
          tooltip={isSnapshot ? "Grantees with grants approved in FY" : "Grantees with grants active (by date overlap) in FY"}
        />
        <StatCard
          title="States & UTs Covered"
          value={summary?.states_covered}
          icon={MapPin}
          color="green"
          loading={loading}
          onClick={() => navigate("/geographic")}
          subtitle={sf ? "From FLUXX" : "Requires Snowflake"}
        />
        <StatCard
          title="Total Sanctioned"
          value={summary?.total_allocation_cr ? fmtCr(summary.total_allocation_cr) : null}
          icon={IndianRupee}
          color="red"
          loading={loading}
          onClick={() => navigate("/financial")}
          subtitle={isSnapshot ? (sf ? "Budget committed in FY" : "Requires Snowflake") : (sf ? "For active grants" : "Requires Snowflake")}
          tooltip={isSnapshot ? "Sum of amount_recommended for grants approved in FY" : "Total sanctioned amount for grants active in FY (date overlap)"}
        />
        <StatCard
          title={isSnapshot ? "Lifetime Disbursement" : "Disbursed for Active Grants"}
          value={summary?.total_disbursement_cr ? fmtCr(summary.total_disbursement_cr) : null}
          icon={TrendingUp}
          color="green"
          loading={loading}
          onClick={() => navigate("/financial")}
          subtitle={isSnapshot ? "Total payout for FY portfolio" : "Actual payments in FY"}
          tooltip={isSnapshot ? "Total payments across all years for grants approved in FY" : "Payments made during selected FY only"}
        />
      </div>

      {/* Financial Summary Row */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="financial-summary-row">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Financial Summary</div>
          <div className={`text-xs px-2 py-0.5 rounded-full font-medium ${isSnapshot ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"}`}>
            {isSnapshot ? "Snapshot Mode" : "Cash Flow Mode"}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            {
              label: "Sanctioned",
              val: summary?.total_allocation_cr ? fmt(summary.total_allocation_cr) : "–",
              note: isSnapshot ? (sf ? "Committed in FY" : "Snowflake req.") : (sf ? "Active grants" : "Snowflake req.")
            },
            {
              label: isSnapshot ? "Lifetime Disbursed" : "Disbursed in FY",
              val: fmt(summary?.total_disbursement_cr),
              note: isSnapshot ? "All-time for portfolio" : "Paid in selected FY"
            },
            {
              label: "% Disbursed",
              val: summary?.disbursement_rate ? `${summary.disbursement_rate}%` : "–",
              note: sf ? "FLUXX/PAY" : "Snowflake req."
            },
            { label: "Utilisation", val: fmt(summary?.total_utilization_cr), note: "From UTIL" },
            { label: "% Utilised", val: summary?.utilization_rate ? `${summary.utilization_rate}%` : "–", note: "Util/Disb" },
          ].map(item => (
            <div key={item.label} className="text-center">
              <div className="text-lg font-bold text-slate-900">{loading ? "..." : item.val}</div>
              <div className="text-xs text-slate-500 font-medium">{item.label}</div>
              {item.note && <div className="text-xs text-slate-600">{item.note}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* YoY Comparison Panel */}
      {yoyData && yoyData.prev_fy && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="yoy-panel">
          <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Year-over-Year Comparison</div>
              <div className="text-xs text-slate-600 mt-0.5 font-mono">
                {yoyData.prev_fy} <span className="text-slate-500 mx-1">→</span> <span className="text-slate-700 font-semibold">{yoyData.current_fy}</span>
              </div>
            </div>
            <div className={`text-xs px-2 py-0.5 rounded-full font-medium ${isSnapshot ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"}`}>
              {isSnapshot ? "Portfolio Snapshot" : "Cash Flow"}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-slate-100">
            {[
              {
                key: "grants",
                label: isSnapshot ? "Grants Approved" : "Grants with Activity",
                curr: yoyData.current?.grants,
                prev: yoyData.prev?.grants,
                delta: yoyData.deltas?.grants,
                fmt: v => v,
                higherIsBetter: true,
              },
              {
                key: "allocation",
                label: "Sanctioned",
                curr: yoyData.current?.allocation_cr,
                prev: yoyData.prev?.allocation_cr,
                delta: yoyData.deltas?.allocation_cr,
                fmt: v => v != null ? fmtCr(v) : "–",
                higherIsBetter: true,
              },
              {
                key: "disbursement",
                label: isSnapshot ? "Lifetime Disbursed" : "Disbursed in FY",
                curr: yoyData.current?.disbursement_cr,
                prev: yoyData.prev?.disbursement_cr,
                delta: yoyData.deltas?.disbursement_cr,
                fmt: v => v != null ? fmtCr(v) : "–",
                higherIsBetter: true,
              },
              {
                key: "disb_rate",
                label: "Disbursement Rate",
                curr: yoyData.current?.disbursement_rate,
                prev: yoyData.prev?.disbursement_rate,
                delta: yoyData.deltas?.disbursement_rate,
                fmt: v => v != null ? `${v}%` : "–",
                higherIsBetter: true,
              },
            ].map(({ key, label, curr, prev, delta, fmt, higherIsBetter }) => {
              const prevPct = curr != null && prev != null && prev !== 0
                ? Math.min(100, Math.abs(curr / prev) * 100) : 0;
              return (
                <div key={key} className="px-5 py-4 relative" data-testid={`yoy-metric-${key}`}>
                  <div className="text-xs text-slate-600 font-medium mb-2">{label}</div>
                  <div className="flex items-end gap-3 mb-2">
                    <div className="text-2xl font-bold text-slate-900">{loading ? "…" : (curr != null ? fmt(curr) : "–")}</div>
                    {!loading && <DeltaBadge delta={delta} higherIsBetter={higherIsBetter} />}
                  </div>
                  <div className="text-xs text-slate-600">
                    vs <span className="font-medium text-slate-600">{prev != null ? fmt(prev) : "–"}</span> in {yoyData.prev_fy}
                  </div>
                  {/* Sparkline bar */}
                  <div className="mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${
                        delta?.pct > 0 ? "bg-green-400" : delta?.pct < 0 ? "bg-red-400" : "bg-slate-300"
                      }`}
                      style={{ width: `${prevPct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sanctioned/Disbursement by Theme */}
        <SectionHeader
          title={sf && isSnapshot ? "Sanctioned by Theme" : "Disbursement by Theme"}
          subtitle={sf && isSnapshot ? "% share of portfolio committed in FY" : "% share of disbursements made in FY"}
        >
          {allocationByTheme.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width={200} height={200}>
                <PieChart>
                  <Pie data={allocationByTheme} dataKey={sf ? "allocation_cr" : "disbursed_cr"} nameKey="theme"
                    cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2}>
                    {allocationByTheme.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, n, p) => [`₹${Number(v).toFixed(2)} Cr (${p.payload.pct}%)`, displayTheme(p.payload.theme)]}
                    contentStyle={{ backgroundColor: "#1E293B", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1.5">
                {allocationByTheme.slice(0, 8).map((item, i) => (
                  <div key={item.theme} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-xs text-slate-600 flex-1 truncate">{displayTheme(item.theme)}</span>
                    <span className="text-xs font-medium text-slate-900">{item.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <div className="py-8 text-center text-sm text-slate-600">Loading...</div>}
        </SectionHeader>

        {/* Disbursement Trend */}
        <SectionHeader title="Disbursement Trend by FY" subtitle="Annual disbursement (with allocation overlay when available)">
          {disbTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={disbTrend} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="fy" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${Number(v).toFixed(2)}`} />
                <Tooltip
                  formatter={(v, n) => [`₹${Number(v).toFixed(2)} Cr`, n === "allocation_cr" ? "Sanctioned" : "Disbursement"]}
                  contentStyle={{ backgroundColor: "#1E293B", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
                />
                <Legend formatter={v => v === "allocation_cr" ? "Sanctioned" : "Disbursement"} wrapperStyle={{ fontSize: 11 }} />
                {sf && <Bar dataKey="allocation_cr" fill="#E2E8F0" radius={[3, 3, 0, 0]} />}
                <Bar dataKey="disbursement_cr" fill="#B71C1C" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="py-8 text-center text-sm text-slate-600">Loading...</div>}
        </SectionHeader>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Overall Grant Health / RAG */}
        <SectionHeader title="Overall Grant Health" tooltip={sf ? "RAG status based on KPI achievement from MEAS data." : "Based on payment status data. Full RAG requires MEAS connection."}>
          {ragSummary ? (
            <div className="space-y-3">
              {sf ? (
                <>
                  {[["Green", "On Track"], ["Amber", "At Risk"], ["Red", "Critical"]].map(([rag, label]) => (
                    <div key={rag} className="flex items-center gap-3">
                      <RAGBadge status={rag} />
                      <div className="flex-1 bg-slate-100 rounded-full h-2">
                        <div className={`h-2 rounded-full ${rag === "Green" ? "bg-green-500" : rag === "Amber" ? "bg-amber-400" : "bg-red-500"}`}
                          style={{ width: `${ragSummary.total > 0 ? (ragSummary[rag] || 0) / ragSummary.total * 100 : 0}%` }} />
                      </div>
                      <span className="text-sm font-medium w-10 text-right">{ragSummary[rag] || 0}</span>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="text-xs text-amber-600 bg-amber-50 rounded-lg p-3 mb-2">
                    Full KPI-based RAG requires Snowflake MEAS. Showing payment status as proxy.
                  </div>
                  {[
                    { label: "Paid", val: ragSummary.on_track, color: "bg-green-500" },
                    { label: "Pending", val: ragSummary.at_risk, color: "bg-amber-400" },
                    { label: "Overdue", val: ragSummary.critical, color: "bg-red-500" },
                  ].map(item => (
                    <div key={item.label} className="flex items-center gap-3">
                      <span className="text-xs text-slate-600 w-16">{item.label}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-2">
                        <div className={`h-2 rounded-full ${item.color}`}
                          style={{ width: `${(item.val / Math.max(ragSummary.on_track + ragSummary.at_risk + ragSummary.critical, 1)) * 100}%` }} />
                      </div>
                      <span className="text-sm font-medium w-10 text-right">{item.val}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          ) : <div className="py-6 text-center text-sm text-slate-600">Loading...</div>}
        </SectionHeader>

        {/* New vs Ongoing */}
        <SectionHeader title="New vs Ongoing Grants" subtitle={newVsOngoing ? `FY: ${newVsOngoing.fy}` : ""}>
          {newVsOngoing ? (
            <div className="flex items-center justify-around py-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#B71C1C]">{newVsOngoing.new}</div>
                <div className="text-xs text-slate-500 mt-1">New Grants</div>
                <div className="text-xs text-slate-600">(Approved this FY)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-700">{newVsOngoing.ongoing}</div>
                <div className="text-xs text-slate-500 mt-1">Ongoing Grants</div>
                <div className="text-xs text-slate-600">(Started before FY)</div>
              </div>
            </div>
          ) : <div className="py-6 text-center text-sm text-slate-600">Loading...</div>}
        </SectionHeader>

        {/* Top Grantees */}
        <SectionHeader
          title={isSnapshot ? "Top Grantees by Sanctioned" : "Top Grantees by Disbursement"}
          subtitle={isSnapshot ? "Ranked by committed budget in FY" : "Ranked by actual payments received"}
        >
          <div className="space-y-2">
            {topGrantees.slice(0, 6).map((g, i) => (
              <div key={i} className="flex items-center gap-3 group hover:bg-slate-50 rounded-lg p-1.5 transition-colors">
                <span className="text-xs font-mono text-slate-600 w-5 text-center">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-slate-900 truncate">{g.grantee}</div>
                  <div className="text-xs text-slate-600">{displayTheme(g.theme)}</div>
                </div>
                <div className="text-xs font-semibold text-[#B71C1C] flex-shrink-0">{fmtCr(g.disbursed_cr)}</div>
              </div>
            ))}
            {topGrantees.length === 0 && <div className="text-sm text-slate-600 text-center py-4">No data</div>}
          </div>
        </SectionHeader>
      </div>

      {/* Pending payments flag */}
      {summary?.pending_payments > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3" data-testid="pending-payments-alert">
          <Clock size={18} className="text-amber-600 flex-shrink-0" />
          <div>
            <span className="text-sm font-medium text-amber-800">{summary.pending_payments} payments pending processing</span>
            <span className="text-xs text-amber-600 ml-2">— click Financial page for details</span>
          </div>
          <button onClick={() => navigate("/financial")} className="ml-auto text-xs text-amber-700 font-medium hover:underline">View →</button>
        </div>
      )}

      {/* Commitment vs Achievement by Theme */}
      {sf && cvaData.length > 0 && (
        <SectionHeader
          title="Commitment vs Achievement by Theme"
          subtitle="Sanctioned · Disbursement · Avg KPI Achievement (line)"
          data-testid="cva-chart"
        >
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={cvaData} margin={{ top: 4, right: 40, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="theme" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" interval={0} />
                <YAxis yAxisId="left" tickFormatter={v => `₹${Number(v).toFixed(2)}`} tick={{ fontSize: 10 }} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} />
                <Tooltip
                  formatter={(v, name) =>
                    name === "avg_kpi_pct" ? [`${Number(v).toFixed(1)}%`, "Avg KPI Achiev."] :
                    name === "disbursed_cr" ? [`₹${Number(v).toFixed(2)} Cr`, "Disbursed"] : [`₹${Number(v).toFixed(2)} Cr`, "Sanctioned"]}
                  contentStyle={{ backgroundColor: "#1E293B", border: "none", borderRadius: 8, color: "#fff", fontSize: 11 }}
                />
                <Legend wrapperStyle={{ fontSize: 10, paddingTop: 8 }}
                  formatter={v => v === "allocation_cr" ? "Sanctioned" : v === "disbursed_cr" ? "Disbursed" : "Avg KPI %"} />
                <Bar yAxisId="left" dataKey="allocation_cr" fill="#E2E8F0" radius={[3,3,0,0]} name="allocation_cr" />
                <Bar yAxisId="left" dataKey="disbursed_cr" fill="#B71C1C" radius={[3,3,0,0]} name="disbursed_cr" />
                <Line yAxisId="right" type="monotone" dataKey="avg_kpi_pct" stroke="#F59E0B" strokeWidth={2.5}
                  dot={{ r: 4, fill: "#F59E0B" }} name="avg_kpi_pct" connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </SectionHeader>
      )}

    </div>
  );
}
